import { useMutation, UseMutationResult } from 'react-query';

import { useAuth } from 'src/hooks';
import { resetPassword } from 'src/utils/urls';

import { ResetPasswordType } from '../types/apiTypes';
import { ApiErrorItem } from 'src/types/ApiErrorType';

export type TResult<TData = unknown> = {
  Errors: ApiErrorItem[];
  ErrorId: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: TData;
};

export type TArguments = ResetPasswordType;
export type TVariable = ResetPasswordType;
export type TError = Error;

export async function setPassword({
  id,
  isNewUserOnBoarding,
  code,
  password,
}: TArguments): Promise<TResult> {
  const response = await fetch(resetPassword(id), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      tenant: process.env.REACT_APP_TENANT_ID || '',
    },
    body: JSON.stringify({
      Token: code,
      Password: password,
      IsNewUserOnBoarding: isNewUserOnBoarding,
    }),
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useResetPassword(): UseMutationResult<
  TResult,
  TError,
  TVariable,
  unknown
> {
  return useMutation(async ({ id, isNewUserOnBoarding, code, password }) => {
    const result = await setPassword({
      id,
      isNewUserOnBoarding,
      code,
      password,
    });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Password reset failed';

      throw new Error(errorMessage);
    }

    return result;
  });
}

export { useResetPassword };
